import React, {useMemo, useEffect, useState, useRef} from 'react'
import {HStack, VStack, Grid, Text, GridItem, useMultiStyleConfig} from '@chakra-ui/react'
import {CustomStyle} from '_uiComponents'
import {useAnalytics, useIsElementInViewport, useViewport} from '_customHooks'
import {useDrawer} from '_context'
import {WallMixedImage} from './partial/image'
import {WallMixedVideo} from './partial/video'
import {WallMixedCarousel} from './partial/carousel'
import {CLDImageAsset, CLDVideoAsset} from '_types'
import {PAGE_DESIGNER_MODULES, stableKeyFromComponent} from '_utils'
import {toEditorialAnalyticsList} from '_analytics/utils/analyticsUtils'

export interface WallMixedComponentData {
    product?: string
    image_link?: string
    video_link?: string
    button_style?: 'button' | 'text' | 'text-cta' | 'outline' | 'blank'
    cld_image_desktop?: CLDImageAsset
    cld_image_tablet?: CLDImageAsset
    cld_image_mobile?: CLDImageAsset
    iframe_id?: CLDVideoAsset
    iframem_id?: CLDVideoAsset
    content_color?: string
    image_title?: string
    title?: string
    animation_enabled?: boolean
    content_t_align?: string
    content_h_align?: string
    content_v_align?: string
    animation_cta_text?: boolean
    animation_text?: boolean
    animation_duration?: string
    image_padding?: string
    video_padding?: string
    text?: string
    button_text?: string
    button_background?: string
    button_color?: string
    /** WCAG 1.4.5 – Live text equivalent for burned-in text in the image */
    accessibility_image_livetext?: string
    /** Item-level custom CSS from Page Designer (data.custom_css). */
    custom_css?: string
    /** Item-level CSS class names from Page Designer (data.class_name), e.g. "back font". */
    class_name?: string
    image_hover_zoom?: boolean
}

export interface WallMixedComponent {
    data?: WallMixedComponentData
    id?: string
    typeId?: string
}

export interface WallMixedRegion {
    components?: WallMixedComponent[]
    id?: string
}

export interface WallMixedProps {
    data: {
        columns_number: string
        layout_text_below_image: boolean
        mobile_side_by_side: boolean
        mobile_carousel?: boolean
        title?: string
        /** Page Designer opt-in ("Rimuovi padding verticale"). Default off. */
        remove_vertical_padding?: boolean
    }
    regions: WallMixedRegion[]
    positionStart: number // Global starting position from Page Designer; internal items increment from this base.
    analyticsID?: string
    analyticsList?: string
    listingLayout?: string
    analyticsName?: string
    adaptiveHeight?: boolean
    /** Module-level custom CSS from Page Designer (data.custom_css), applied on the wrapper like every other PD module. */
    extraCSS?: string
}
const WallMixed: React.FC<WallMixedProps> = ({
    data,
    regions,
    positionStart,
    analyticsID,
    analyticsList,
    listingLayout,
    analyticsName,
    adaptiveHeight,
    extraCSS,
}) => {
    const {isMobile, isTablet} = useViewport()
    const {isOpen: isDrawerOpen} = useDrawer()
    const analyticsListEditorial = toEditorialAnalyticsList(analyticsList)
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const {analyticsEventPromoView} = useAnalytics()
    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)
    const styles = useMultiStyleConfig('WallMixed', {
        mobileSideBySide: data.mobile_side_by_side,
        carousel: data.mobile_carousel,
        adaptiveHeight,
        removeVerticalPadding: data.remove_vertical_padding === true,
    }) as Record<
        string,
        any
    >
    const computeColumns = useMemo(() => {
        return () => {
            if (!isMobile) {
                return parseInt(data.columns_number, 10) || 1
            }
            return data.mobile_side_by_side ? 2 : 1
        }
    }, [data.columns_number, data.mobile_side_by_side, isMobile, isTablet])

    const [columns, setColumns] = useState<number>(() => computeColumns())

    useEffect(() => {
        if (isDrawerOpen) return
        setColumns(computeColumns())
    }, [computeColumns, isDrawerOpen])
    const components = regions[0]?.components || []

    // Collect the custom_css of every child item and render it once at module level. In Page Designer
    // the CSS (including the @font-face and the global `.font.cc-pd-wall-mixed-image ...` rules) is
    // often authored on a single item, but its selectors are global and meant to style ALL items in
    // the module. Hoisting avoids relying on which item carries it and prevents duplicate <style> tags.
    const itemsCustomCss = useMemo(
        () =>
            components
                .map((c) => (c.data as {custom_css?: string})?.custom_css)
                .filter((css): css is string => Boolean(css && css.trim()))
                .join('\n'),
        [components],
    )

    useEffect(() => {
        if (isVisible && !alreadySentPromoView) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: positionStart,
                creative: PAGE_DESIGNER_MODULES.WALL_MIXED,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isVisible])

    const rows = useMemo(() => {
        const nextRows: WallMixedComponent[][] = []
        for (let i = 0; i < components.length; i += columns) {
            nextRows.push(components.slice(i, i + columns))
        }
        return nextRows
    }, [components, columns])
    let rowGap = 4
    if ((isMobile || isTablet) && !data.layout_text_below_image) {
        rowGap = 0
    }

    if (isMobile && data.mobile_carousel) {
        return (
            <HStack {...styles.wallMixedWrapper} data-testid="wall-mixed-container" className="cc-pd cc-pd-wall-mixed" ref={targetElement}>
                <CustomStyle css={[extraCSS, itemsCustomCss].filter(Boolean).join('\n')} />
                <VStack {...styles.header} className="cc-section-title">
                    {data.title && (
                        <Text {...styles.title} className="cc-title">
                            {data.title}
                        </Text>
                    )}
                </VStack>
                <WallMixedCarousel
                    components={components}
                    layout_text_below_image={data.layout_text_below_image}
                    positionStart={positionStart}
                    analyticsID={analyticsID}
                    analyticsList={analyticsListEditorial}
                    listingLayout={listingLayout}
                    analyticsName={analyticsName}
                    adaptiveHeight={adaptiveHeight}
                />
            </HStack>
        )
    }

    return (
        <HStack {...styles.wallMixedWrapper} data-testid="wall-mixed-container" className="cc-pd cc-pd-wall-mixed" data-columns={columns} ref={targetElement}>
            <CustomStyle css={[extraCSS, itemsCustomCss].filter(Boolean).join('\n')} />
            <VStack {...styles.header} className="cc-section-title">
                {data.title && (
                    <Text {...styles.title} className="cc-title">
                        {data.title}
                    </Text>
                )}
            </VStack>
            <VStack w="100%" align="stretch" gap={rowGap}>
                {rows.map((row, rowIdx) => (
                    <Grid
                        key={row.map((component) => component.id || stableKeyFromComponent(component) || '').join('|') || rowIdx}
                        templateColumns={`repeat(${row.length}, 1fr)`}
                        w="100%"
                        {...(!data.layout_text_below_image ? styles.gridContainer : undefined)}
                        {...((row.length === 3 || row.length === 1) && data.layout_text_below_image ? styles.gridThreeCol : undefined)}
                        {...((row.length === 4 || row.length === 1 || row.length === 2) && data.layout_text_below_image ? styles.gridFourCol : undefined)}
                    >
                        {row.map((component, colIdx) => {
                            return (
                                <GridItem key={component.id || stableKeyFromComponent(component) || `${rowIdx}-${colIdx}`} w="100%">
                                    {component.typeId?.indexOf(PAGE_DESIGNER_MODULES.WALL_MIXED_IMAGE) !== -1 && (
                                        <WallMixedImage
                                            cld_image_desktop={component.data?.cld_image_desktop}
                                            cld_image_mobile={component.data?.cld_image_mobile}
                                            cld_image_tablet={component.data?.cld_image_tablet}
                                            title={component.data?.title}
                                            text={component.data?.text}
                                            text_align={component.data?.content_t_align}
                                            h_align={component.data?.content_h_align}
                                            v_align={component.data?.content_v_align}
                                            ctaText={component.data?.button_text}
                                            ctaStyle={component.data?.button_style}
                                            animation_enabled={component.data?.animation_enabled}
                                            animation_cta_text={component.data?.animation_cta_text}
                                            animation_text={component.data?.animation_text}
                                            animation_duration={component.data?.animation_duration}
                                            image_padding={component.data?.image_padding}
                                            layout_text_below_image={data.layout_text_below_image}
                                            button_background={component.data?.button_background}
                                            content_color={component.data?.content_color}
                                            button_color={component.data?.button_color}
                                            productID={component.data?.product}
                                            position={positionStart}
                                            analyticsID={analyticsID}
                                            image_link={component.data?.image_link}
                                            analyticsList={analyticsListEditorial}
                                            listingLayout={listingLayout}
                                            mobileSideBySide={data?.mobile_side_by_side}
                                            analyticsName={analyticsName}
                                            imageZoomHover={component.data?.image_hover_zoom}
                                            adaptiveHeight={adaptiveHeight}
                                            parentColsNumber={data?.columns_number}
                                            horizontal_index={rowIdx * columns + colIdx}
                                            accessibility_image_livetext={component.data?.accessibility_image_livetext}
                                            classes={component.data?.class_name}
                                        />
                                    )}
                                    {component.typeId?.indexOf(PAGE_DESIGNER_MODULES.WALL_MIXED_VIDEO) !== -1 && (
                                        <WallMixedVideo
                                            iframe_id={component.data?.iframe_id}
                                            iframem_id={component.data?.iframem_id}
                                            title={component.data?.title}
                                            layout_text_below_image={data.layout_text_below_image}
                                            position={positionStart}
                                            video_padding={component.data?.video_padding}
                                            text_align={component.data?.content_t_align}
                                            h_align={component.data?.content_h_align}
                                            v_align={component.data?.content_v_align}
                                            ctaText={component.data?.button_text}
                                            text={component.data?.text}
                                            ctaStyle={component.data?.button_style}
                                            button_background={component.data?.button_background}
                                            content_color={component.data?.content_color}
                                            button_color={component.data?.button_color}
                                            productID={component.data?.product}
                                            analyticsID={analyticsID}
                                            video_link={component.data?.video_link} //sarà video link
                                            analyticsList={analyticsListEditorial}
                                            listingLayout={listingLayout}
                                            animation_enabled={component.data?.animation_enabled}
                                            animation_cta_text={component.data?.animation_cta_text}
                                            animation_text={component.data?.animation_text}
                                            mobileSideBySide={data?.mobile_side_by_side}
                                            analyticsName={analyticsName}
                                            adaptiveHeight={adaptiveHeight}
                                            parentColsNumber={data?.columns_number}
                                            horizontal_index={rowIdx * columns + colIdx}
                                        />
                                    )}
                                </GridItem>
                            )
                        })}
                    </Grid>
                ))}
            </VStack>
        </HStack>
    )
}

export {WallMixed}
